import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import UploadArea from "./UploadArea";
import AssinarPdfModal from "./AssinarPdfModal";
import { ehImagemAssinavel, imagemParaPdf, nomeSemExtensao } from "../../../utils/solicitacoes/imagemParaPdf";

function ehPdf(nome = "") {
    return nome.split(".").pop()?.toLowerCase() === "pdf";
}

function normalizar(texto = "") {
    return texto.trim().toLowerCase();
}

// Um botão por tipo obrigatório — ao clicar, já abre a seleção de arquivo/foto (a câmera do
// celular, no navegador mobile) e usa o próprio tipo como descrição automaticamente, sem exigir
// que o usuário digite ou escolha nada depois.
function BotaoAnexoTipo({ tipo, atendido, onSelecionar }) {
    const inputRef = useRef(null);

    function aoSelecionarArquivos(e) {
        const arquivos = Array.from(e.target.files || []);
        if (arquivos.length > 0) onSelecionar(arquivos);
        e.target.value = "";
    }

    return (
        <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm text-left transition-colors duration-150
                ${atendido ? "border-success/40 bg-success/5 text-success" : "border-border hover:border-primary/50 hover:bg-muted text-foreground"}
            `}
        >
            <i className={`fa ${atendido ? "fa-check-circle" : "fa-camera"} shrink-0`} />
            <span className="flex-1">{tipo}</span>
            <input ref={inputRef} type="file" accept="image/*,.pdf" className="hidden" onChange={aoSelecionarArquivos} />
        </button>
    );
}

// Seleção de anexos ANTES de existir uma solicitação (fica em memória até o cadastro ser enviado).
// `itensExtras` é opcional: anexos que já existem em outro lugar do formulário (ex.: a imagem
// anexada a um produto, na Solicitação de Compras) e que devem aparecer aqui só pra visibilidade —
// removê-los aciona `onRemoverExtra`, que quem chamou decide o que fazer (ex.: limpar o campo do
// produto), já que `itens`/`onChange` continuam sendo só a lista de anexos "genéricos".
// `anexosObrigatorios` é opcional: lista de descrições (ex.: ["Documento", "Foto do PDV"]) que
// precisam ter pelo menos um anexo cada — usado quando o processo exige tipos específicos de
// anexo, não só "pelo menos um qualquer" (ver NovaSolicitacaoCadastroClienteForm). Quando
// informado, essas descrições também viram as sugestões do campo de descrição.
export default function StagedAnexosEditor({ itens = [], onChange, obrigatorio = false, sugestoes = ["Outros"], rateioItens, rateioItensDetalhados, formaPagamento, itensExtras = [], onRemoverExtra, anexosObrigatorios = [] }) {
    const [indiceAssinando, setIndiceAssinando] = useState(null);

    function adicionar(arquivos, descricao) {
        onChange([...itens, ...arquivos.map(arquivo => ({ arquivo, descricao }))]);
    }

    const todosItens = [...itens, ...itensExtras];
    const tiposFaltando = anexosObrigatorios.filter(
        tipo => !todosItens.some(item => normalizar(item.descricao) === normalizar(tipo))
    );

    function remover(index) {
        onChange(itens.filter((_, i) => i !== index));
    }

    const itemAssinando = indiceAssinando != null ? itens[indiceAssinando] : null;

    // Imagem escolhida pra assinar: vira PDF de uma página (null enquanto converte — o modal mostra o carregamento).
    const [pdfDaImagem, setPdfDaImagem] = useState(null);
    const assinandoImagem = !!itemAssinando && ehImagemAssinavel(itemAssinando.arquivo.name);
    useEffect(() => {
        setPdfDaImagem(null);
        if (!assinandoImagem) return undefined;
        let cancelado = false;
        imagemParaPdf(itemAssinando.arquivo)
            .then(pdf => { if (!cancelado) setPdfDaImagem(pdf); })
            .catch(error => {
                console.error("Erro ao converter imagem para assinatura", error);
                toast.error("Não foi possível abrir a imagem para assinatura.");
                if (!cancelado) setIndiceAssinando(null);
            });
        return () => { cancelado = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [indiceAssinando, assinandoImagem]);

    const documentoAssinando = itemAssinando ? {
        titulo: `Assinar: ${itemAssinando.arquivo.name}`,
        nomeArquivoSaida: `assinado_${assinandoImagem ? `${nomeSemExtensao(itemAssinando.arquivo.name)}.pdf` : itemAssinando.arquivo.name}`,
        blob: assinandoImagem ? pdfDaImagem : itemAssinando.arquivo,
    } : null;

    const totalItens = itens.length + itensExtras.length;

    return (
        <div className="flex flex-col gap-2">
            {anexosObrigatorios.length > 0 && (
                <div className="flex flex-col gap-1.5">
                    {anexosObrigatorios.map(tipo => (
                        <BotaoAnexoTipo
                            key={tipo}
                            tipo={tipo}
                            atendido={!tiposFaltando.includes(tipo)}
                            onSelecionar={arquivos => adicionar(arquivos, tipo)}
                        />
                    ))}
                </div>
            )}

            {obrigatorio && anexosObrigatorios.length > 0 && tiposFaltando.length > 0 && (
                <p className="text-xs text-destructive">
                    <i className="fa fa-exclamation-circle mr-1" />
                    Ainda faltam anexar: {tiposFaltando.join(", ")}.
                </p>
            )}

            {anexosObrigatorios.length > 0 && (
                <label className="text-xs font-medium text-muted-foreground/80 mt-1">Outros anexos (opcional)</label>
            )}
            <UploadArea onArquivoSelecionado={adicionar} sugestoes={sugestoes} />

            {obrigatorio && anexosObrigatorios.length === 0 && totalItens === 0 && (
                <p className="text-xs text-destructive">
                    <i className="fa fa-exclamation-circle mr-1" />
                    É necessário anexar pelo menos um documento para concluir esta etapa.
                </p>
            )}

            {totalItens > 0 && (
                <div className="flex flex-col gap-1.5">
                    {itens.map((item, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 border border-border rounded-lg bg-muted text-sm">
                            <i className="fa fa-paperclip text-primary shrink-0" />
                            <span className="flex-1 truncate" title={item.arquivo.name}>{item.arquivo.name}</span>
                            <span className="shrink-0 text-[10px] font-semibold bg-primary/10 text-primary rounded-md px-1.5 py-0.5">
                                {item.descricao}
                            </span>
                            {(ehPdf(item.arquivo.name) || ehImagemAssinavel(item.arquivo.name)) && (
                                <button
                                    type="button"
                                    title="Assinar documento"
                                    onClick={() => setIndiceAssinando(index)}
                                    className="shrink-0 text-primary hover:bg-primary/10 rounded-full p-1"
                                >
                                    <i className="fa fa-signature" />
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => remover(index)}
                                className="shrink-0 text-destructive hover:bg-destructive/10 rounded-full p-1"
                            >
                                <i className="fa fa-times" />
                            </button>
                        </div>
                    ))}
                    {itensExtras.map((item, index) => (
                        <div key={`extra-${index}`} className="flex items-center gap-2 p-2 border border-border rounded-lg bg-muted text-sm">
                            <i className="fa fa-paperclip text-primary shrink-0" />
                            <span className="flex-1 truncate" title={item.arquivo.name}>{item.arquivo.name}</span>
                            <span className="shrink-0 text-[10px] font-semibold bg-primary/10 text-primary rounded-md px-1.5 py-0.5">
                                {item.descricao}
                            </span>
                            <button
                                type="button"
                                title="Remover"
                                onClick={() => onRemoverExtra?.(index)}
                                className="shrink-0 text-destructive hover:bg-destructive/10 rounded-full p-1"
                            >
                                <i className="fa fa-times" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <AssinarPdfModal
                documento={documentoAssinando}
                rateioItens={rateioItens}
                rateioItensDetalhados={rateioItensDetalhados}
                formaPagamento={formaPagamento}
                onClose={() => setIndiceAssinando(null)}
                onConfirmar={async arquivoAssinado => {
                    const novosItens = [...itens];
                    novosItens[indiceAssinando] = { ...novosItens[indiceAssinando], arquivo: arquivoAssinado };
                    onChange(novosItens);
                    toast.success("Documento assinado!");
                }}
            />
        </div>
    );
}
